import { useState, useEffect, useRef } from "react";
import { HoverCard } from "./HoverCard";
import { useWindowSize } from "@hooks/useWindowResize";
import { getProductURL } from "@services/product";
import { Product } from "@components/ProductCard";
import styles from "./styles/StyledWith.module.css";

const formatTitle = (str) => {
  const wordsWithApostrophes = /^([\w]+['’][\w]+)\s/g;
  return str.replace(wordsWithApostrophes, "");
};

const MAX_WIDTH = 720;

const ListItem = ({ products, product, index }) => {
  const [redisProduct, setRedisProduct] = useState();
  const [hoverState, setHoverState] = useState(false);
  const [listItemRect, setListItemRect] = useState({ bottom: 0, left: 0 });
  const { width } = useWindowSize();
  const ref = useRef();

  const { productColour, productReferenceV2 } = product;

  // fetch product from redis
  useEffect(() => {
    fetchRedisProduct();

    return () => {
      setRedisProduct(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRedisProduct = async () => {
    const handle = productReferenceV2.store?.slug.current;
    if (handle) {
      const productFromRedis = await getProductURL(handle);
      if (productFromRedis) {
        setRedisProduct(productFromRedis);
      }
    }
  };

  const path = redisProduct && `/products/${redisProduct.url.slug}`;

  const colour = redisProduct && redisProduct.url.colour;
  const colourQuery = `colour=${colour}`;
  const link = `${path}/?${colourQuery}`;

  const handleHover = () => {
    const listItem = ref.current.getBoundingClientRect();
    setListItemRect({ bottom: listItem.bottom, left: listItem.left });
    setHoverState(true);
  };

  if (redisProduct) {
    return (
      <li
        className={styles.listItem}
        onMouseEnter={handleHover}
        onMouseLeave={() => setHoverState(false)}
        ref={ref}
      >
        <a href={link} className={styles.link}>
          {index !== products.length - 1
            ? `${formatTitle(redisProduct.title)},`
            : `${formatTitle(redisProduct.title)}`}
        </a>
        {width > MAX_WIDTH && (
          <HoverCard
            hoverState={hoverState}
            product={redisProduct}
            productColour={productColour}
            link={link}
            listItemRect={listItemRect}
          />
        )}
      </li>
    );
  }

  return null;
};

const GridItem = ({ product, index }) => {
  const [redisProduct, setRedisProduct] = useState();

  // fetch product from redis
  useEffect(() => {
    fetchRedisProduct();
    return () => setRedisProduct(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRedisProduct = async () => {
    const handle = product.productReferenceV2.store?.slug.current;
    if (handle) {
      const productFromRedis = await getProductURL(handle);
      if (productFromRedis) {
        setRedisProduct(productFromRedis);
      }
    }
  };

  if (redisProduct) {
    return (
      <li>
        <Product product={redisProduct} listPosition={index} key={`${product.id}-${index}`} />
      </li>
    );
  }

  return null;
};

export const List = ({ products }) => {
  return (
    <ul>
      {products.map((product, index) => {
        return (
          <ListItem
            key={product.productReferenceV2._id}
            products={products}
            product={product}
            index={index}
          />
        );
      })}
    </ul>
  );
};

export const GridList = ({ products }) => {
  const [isHidden, setIsHidden] = useState(true);

  const firstTwoProducts = products.slice(0, 2);
  const lastTwoProducts = products.slice(2, 4);

  const ShowMoreButton = () => {
    return (
      <button
        className={styles.button}
        onClick={() => {
          setIsHidden(!isHidden);
        }}
      >
        {isHidden ? "View More" : "View Less"}
      </button>
    );
  };

  return (
    <>
      <ul className={styles.grid}>
        {firstTwoProducts.map((product, index) => {
          return <GridItem key={product.productReferenceV2._id} product={product} index={index} />;
        })}
        {!isHidden &&
          lastTwoProducts.map((product, index) => {
            return (
              <GridItem key={product.productReferenceV2._id} product={product} index={index} />
            );
          })}
      </ul>
      {products.length > 2 && <ShowMoreButton />}
    </>
  );
};
